<template>
  <v-dialog
    :value="value"
    max-width="1072"
    persistent
    scrollable
    data-testid="add-edit-request-modal"
    @input="$emit('input', $event)"
  >
    <v-card class="add-edit-request-modal">
      <v-card-title class="add-edit-request-modal__header white--text">
        <span>{{ isEdit ? 'Edit Request' : 'New Request' }}</span>
        <v-spacer />
        <v-btn
          icon
          dark
          data-testid="add-edit-request-modal__close-btn"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="add-edit-request-modal__body">
        <v-row dense>
          <v-col cols="12" class="add-edit-request-modal__field">
            <div class="add-edit-request-modal__field-header">
              <label class="add-edit-request-modal__label">
                Requested information <span class="add-edit-request-modal__required">*</span>
              </label>
            </div>
            <lb-string
              :value="form.description"
              label=""
              placeholder="e.g. Bank statements — operating account"
              :error="errors.description"
              data-testid="add-edit-request-modal__description"
              @input="updateField('description', $event)"
            />
          </v-col>

          <v-col cols="12" class="add-edit-request-modal__field">
            <div class="add-edit-request-modal__field-header">
              <label class="add-edit-request-modal__label">
                Requested From <span class="add-edit-request-modal__required">*</span>
              </label>
              <span class="add-edit-request-modal__hint">
                Who should provide this — the information owner you're requesting from
              </span>
            </div>
            <lb-dropdown
              :items="formattedOwnerList"
              itemtext="displayName"
              itemvalue="_id"
              :value="form.info_owner"
              label=""
              placeholder="Search information owners..."
              prependinnericon="mdi-magnify"
              :error="errors.info_owner"
              data-testid="add-edit-request-modal__info-owner"
              @input="updateField('info_owner', $event)"
            />
          </v-col>

          <v-col cols="12" class="add-edit-request-modal__field">
            <div class="add-edit-request-modal__field-header">
              <label class="add-edit-request-modal__label">
                Response Type <span class="add-edit-request-modal__required">*</span>
              </label>
              <span class="add-edit-request-modal__hint">What the owner is asked to provide</span>
            </div>
            <div class="add-edit-request-modal__type-cards">
              <button
                v-for="typeOption in responseTypeOptions"
                :key="typeOption.value"
                type="button"
                class="add-edit-request-modal__type-card"
                :class="{
                  'add-edit-request-modal__type-card--selected': form.type === typeOption.value,
                }"
                :data-testid="`add-edit-request-modal__type-${typeOption.value}`"
                @click="selectType(typeOption.value)"
              >
                <span
                  class="add-edit-request-modal__type-icon"
                  :style="{ width: `${typeOption.iconBox}px`, height: `${typeOption.iconBox}px` }"
                  aria-hidden="true"
                >
                  <img
                    :src="typeOption.icon"
                    :alt="''"
                    :width="typeOption.iconSize"
                    :height="typeOption.iconSize"
                  >
                </span>
                <span class="add-edit-request-modal__type-text">
                  <span class="add-edit-request-modal__type-label">{{ typeOption.label }}</span>
                  <span class="add-edit-request-modal__type-subtitle">{{ typeOption.subtitle }}</span>
                </span>
                <v-icon
                  v-if="form.type === typeOption.value"
                  class="add-edit-request-modal__type-check"
                  color="primary"
                  size="22"
                >
                  mdi-check-circle
                </v-icon>
              </button>
            </div>
            <p v-if="errors.type" class="error--text caption mt-1 mb-0">{{ errors.type }}</p>
          </v-col>

          <v-col v-if="form.type === 'link'" cols="12" class="add-edit-request-modal__field">
            <div class="add-edit-request-modal__link-input">
              <img
                src="/img/icons/fbc-icons/LinkInputIcon.svg"
                width="16"
                height="16"
                alt=""
                class="add-edit-request-modal__link-input-icon"
                aria-hidden="true"
              >
              <lb-string
                :value="form.uploadlink"
                label=""
                placeholder="Paste your Link here"
                :error="errors.uploadlink"
                :isClearable="false"
                class="add-edit-request-modal__link-input-field"
                data-testid="add-edit-request-modal__uploadlink"
                @input="updateField('uploadlink', $event)"
              />
            </div>
          </v-col>

          <v-col cols="12" md="4" class="add-edit-request-modal__field">
            <label class="add-edit-request-modal__label">
              Priority <span class="add-edit-request-modal__required">*</span>
            </label>
            <lb-dropdown
              :items="priorityList"
              :value="form.priority"
              label=""
              :error="errors.priority"
              data-testid="add-edit-request-modal__priority"
              @input="updateField('priority', $event)"
            />
          </v-col>

          <v-col cols="12" md="4" class="add-edit-request-modal__field">
            <label class="add-edit-request-modal__label">
              Classification <span class="add-edit-request-modal__required">*</span>
            </label>
            <lb-dropdown
              :key="`category-${request?._id || 'new'}`"
              v-model="form.category"
              :items="categoryList"
              :customvalue="true"
              :searchable="false"
              :attach="false"
              appendiconinner="mdi-chevron-down"
              label=""
              placeholder="Select or enter classification"
              :error="errors.category"
              data-testid="add-edit-request-modal__category"
              @change="updateField('category', $event)"
            />
          </v-col>

          <v-col cols="12" md="4" class="add-edit-request-modal__field">
            <label class="add-edit-request-modal__label">
              Due date <span class="add-edit-request-modal__required">*</span>
            </label>
            <lb-date
              :value="form.duedate"
              label="Due Date"
              label-as-hint
              :error="errors.duedate"
              :min="minDate"
              :max="maxDate"
              data-testid="add-edit-request-modal__duedate"
              @input="updateField('duedate', $event)"
            />
          </v-col>

          <v-col cols="12" class="add-edit-request-modal__field">
            <label class="add-edit-request-modal__label">
              {{ locationHeader }} <span class="add-edit-request-modal__required">*</span>
            </label>
            <ChipMultiSelect
              :value="form.location"
              :items="actorLocationItems"
              :loading="actorLocationsLoading"
              :single="true"
              :clearable="true"
              :searchable="true"
              search-placeholder="Search"
              item-text="name"
              item-value="_id"
              label=""
              placeholder="Select"
              :error="errors.location"
              data-testid="add-edit-request-modal__location"
              data-testid-prefix="add-edit-request-modal__location"
              @input="onLocationIdsChange"
            />
          </v-col>

          <v-col cols="12" class="add-edit-request-modal__field">
            <div
              class="add-edit-request-modal__tasks-header d-flex align-center justify-space-between"
              @click="tasksExpanded = !tasksExpanded"
            >
              <div class="d-flex align-center">
                <span class="add-edit-request-modal__label mb-0">Link tasks</span>
                <span class="add-edit-request-modal__hint ml-1">(optional)</span>
              </div>
              <v-btn icon small data-testid="add-edit-request-modal__tasks-toggle">
                <v-icon size="20">{{ tasksExpanded ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
              </v-btn>
            </div>

            <div v-if="selectedTaskItems.length > 0" class="add-edit-request-modal__task-chips">
              <v-chip
                v-for="item in selectedTaskItems"
                :key="item.value"
                class="add-edit-request-modal__task-chip"
                close
                close-icon="mdi-close-circle"
                data-testid="add-edit-request-modal__task-chip"
                @click:close="removeTask(item.value)"
              >
                <span class="add-edit-request-modal__task-chip-text">
                  {{ item.ref }} · {{ truncateTaskName(item.name) }}
                </span>
              </v-chip>
            </div>

            <div v-if="tasksExpanded" class="add-edit-request-modal__tasks-panel">
              <SearchBar
                :value="taskSearchQuery"
                placeholder="Search Tasks"
                width="100%"
                data-testid-prefix="add-edit-request-modal__task-search"
                class="add-edit-request-modal__task-search"
                @input="onTaskSearch"
              />
              <div
                class="add-edit-request-modal__tasks-table-wrap"
                data-testid="add-edit-request-modal__task-table"
                @scroll="onTaskScroll"
              >
                <table class="add-edit-request-modal__tasks-table">
                  <thead>
                    <tr>
                      <th class="add-edit-request-modal__tasks-th add-edit-request-modal__tasks-th--check" scope="col">
                        <v-checkbox
                          class="ma-0 pa-0"
                          hide-details
                          dense
                          color="primary"
                          :input-value="allTasksSelected"
                          data-testid="add-edit-request-modal__toggle-all"
                          @change="toggleSelectAll"
                        />
                      </th>
                      <th class="add-edit-request-modal__tasks-th add-edit-request-modal__tasks-th--ref" scope="col">
                        Ref
                      </th>
                      <th class="add-edit-request-modal__tasks-th" scope="col">Task name</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(account, idx) in filteredTaskList"
                      :key="account.value || idx"
                      :class="{ 'add-edit-request-modal__task-row--selected': internalSelectedTasks.includes(account.value) }"
                      data-testid="add-edit-request-modal__task-row"
                    >
                      <td class="add-edit-request-modal__tasks-td add-edit-request-modal__tasks-td--check">
                        <v-checkbox
                          class="ma-0 pa-0"
                          dense
                          hide-details
                          color="primary"
                          :input-value="internalSelectedTasks.includes(account.value)"
                          data-testid="add-edit-request-modal__task-checkbox"
                          @change="toggleTask(account.value)"
                        />
                      </td>
                      <td class="add-edit-request-modal__tasks-td add-edit-request-modal__tasks-td--ref">
                        <span class="add-edit-request-modal__task-ref">{{ account.ref }}</span>
                      </td>
                      <td class="add-edit-request-modal__tasks-td">
                        {{ account.name }}
                      </td>
                    </tr>
                    <tr v-if="taskLoading" data-testid="add-edit-request-modal__task-loading">
                      <td colspan="3" class="add-edit-request-modal__tasks-td text-center">
                        <v-progress-circular indeterminate size="20" width="2" color="primary" />
                        <span class="ml-2 caption">Loading...</span>
                      </td>
                    </tr>
                    <tr v-if="!taskLoading && filteredTaskList.length === 0" data-testid="add-edit-request-modal__task-empty">
                      <td colspan="3" class="add-edit-request-modal__tasks-td text-center grey--text">
                        No tasks found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="add-edit-request-modal__footer px-6 pb-6">
        <v-spacer />
        <v-btn
          outlined
          class="add-edit-request-modal__cancel-btn text-capitalize secondaryBtn"
          data-testid="add-edit-request-modal__cancel-btn"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="white--text text-capitalize ml-3"
          :loading="loading"
          data-testid="add-edit-request-modal__submit-btn"
          @click="submit"
        >
          {{ isEdit ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import './AddEditRequestModal.scss';
import { SearchBar, ChipMultiSelect } from '../../../../components';
import { MAX_DUE_DATE_DAYS, INFORMATION_MANAGER_TYPE_OPTIONS } from '../../../../utils/constants';
import { fetchActorLocations, dropIdsNotAllowed } from '../../../../utils/locationUserAccess';

const EMPTY_FORM = {
  description: '',
  instruction: '',
  info_owner: '',
  type: 'fileupload',
  priority: 'Medium',
  category: '',
  duedate: '',
  location: '',
  uploadlink: '',
};

export default {
  name: 'AddEditRequestModal',
  components: {
    SearchBar,
    ChipMultiSelect,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Object,
      default: null,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    infoOwnerList: {
      type: Array,
      default: () => [],
    },
    priorityList: {
      type: Array,
      default: () => ['High', 'Medium', 'Low'],
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
    locationList: {
      type: Array,
      default: () => [],
    },
    locationHeader: {
      type: String,
      default: 'Location',
    },
    minDate: {
      type: String,
      default: '',
    },
    taskOptions: {
      type: Array,
      default: () => [],
    },
    projectId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      form: { ...EMPTY_FORM },
      internalSelectedTasks: [],
      taskSearchQuery: '',
      tasksExpanded: false,
      taskRecords: [],
      taskPage: 1,
      taskLimit: 50,
      taskTotalCount: 0,
      taskLoading: false,
      taskFullyLoaded: false,
      taskInitialized: false,
      actorLocationItems: [],
      actorLocationsLoading: false,
      actorLocationsToken: 0,
    };
  },
  computed: {
    responseTypeOptions() {
      return INFORMATION_MANAGER_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.name,
        subtitle: option.subtitle,
        icon: option.icon,
        iconSize: option.iconSize,
        iconBox: option.iconBox,
      }));
    },
    isEdit() {
      return Boolean(this.request?._id);
    },
    formattedOwnerList() {
      return (this.infoOwnerList || [])
        .filter((owner) => owner.status !== false)
        .map((owner) => ({
          ...owner,
          displayName: owner.email ? `${owner.name} (${owner.email})` : owner.name,
        }));
    },
    selectedTaskItems() {
      const recordMap = new Map(this.taskRecords.map((r) => [r._id, r]));
      const optionMap = new Map((this.taskOptions || []).map((item) => [item.value, item]));
      return this.internalSelectedTasks
        .map((value) => {
          const record = recordMap.get(value);
          if (record) {
            return {
              value: record._id,
              name: record.description,
              ref: record.ref,
              location: record.location,
            };
          }
          return optionMap.get(value);
        })
        .filter(Boolean);
    },
    filteredTaskList() {
      return this.taskRecords.map((task) => ({
        value: task._id,
        name: task.description,
        ref: task.ref,
        location: task.location,
        master_id: task.master_id,
      }));
    },
    allTasksSelected() {
      return (
        this.filteredTaskList.length > 0
        && this.filteredTaskList.every((option) => this.internalSelectedTasks.includes(option.value))
      );
    },
    /** Calendar-day cap from today — not cycle-based working days (see MAX_DUE_DATE_DAYS in constants.js). */
    maxDate() {
      const max = new Date();
      max.setDate(max.getDate() + MAX_DUE_DATE_DAYS);
      return this.$nova?.formatDateISO ? this.$nova.formatDateISO(max) : max.toISOString().split('T')[0];
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
        this.loadActorLocations();
        if (!this.taskInitialized) {
          this.loadTaskFirstPage();
        }
      }
    },
    request: {
      handler() {
        if (this.value) {
          this.resetForm();
        }
      },
      deep: true,
    },
  },
  methods: {
    async loadActorLocations() {
      if (!this.projectId) {
        this.actorLocationItems = [];
        this.actorLocationsLoading = false;
        return;
      }
      const token = ++this.actorLocationsToken;
      this.actorLocationsLoading = true;
      try {
        const list = await fetchActorLocations({ cycleId: this.projectId });
        if (token !== this.actorLocationsToken) return;
        this.actorLocationItems = list;
        const allowed = list.map((loc) => loc._id);
        const kept = dropIdsNotAllowed(this.form.location ? [this.form.location] : [], allowed);
        this.form.location = kept[0] || '';
      } catch (err) {
        if (token !== this.actorLocationsToken) return;
        console.error('[AddEditRequestModal]: actorLocations error', err);
      } finally {
        if (token === this.actorLocationsToken) this.actorLocationsLoading = false;
      }
    },
    locationIdFrom(value) {
      if (Array.isArray(value)) return this.locationIdFrom(value[0]);
      if (value && typeof value === 'object') {
        return value._id != null ? String(value._id) : '';
      }
      if (value === undefined || value === null || value === '') return '';
      return String(value);
    },
    onLocationIdsChange(ids) {
      // ChipMultiSelect single mode emits one id. Keep that id so the chip stays visible.
      this.onLocationChange(this.locationIdFrom(ids));
    },
    resetForm() {
      if (this.request?._id) {
        let type = this.request.type || 'fileupload';
        if (type === 'fileuploadtolink') type = 'link';
        this.form = {
          description: this.request.description || '',
          instruction: this.request.instruction || '',
          info_owner: this.request.info_owner || '',
          type,
          priority: this.request.priority || 'Medium',
          category: this.request.category || '',
          duedate: this.request.duedate || '',
          location: this.locationIdFrom(this.request.location),
          uploadlink: this.request.uploadlink || '',
        };
        this.internalSelectedTasks = Array.isArray(this.request.reference)
          ? [...this.request.reference]
          : [];
        this.tasksExpanded = this.internalSelectedTasks.length > 0;
      } else {
        this.form = {
          ...EMPTY_FORM,
          ...(this.request?.location ? { location: this.locationIdFrom(this.request.location) } : {}),
        };
        this.internalSelectedTasks = Array.isArray(this.request?.reference)
          ? [...this.request.reference]
          : [];
        this.tasksExpanded = this.internalSelectedTasks.length > 0;
      }
      this.taskSearchQuery = '';
    },
    updateField(field, value) {
      this.$set(this.form, field, value);
      this.$emit('clear-error', field);
    },
    selectType(type) {
      this.updateField('type', type);
      if (type !== 'link') {
        this.updateField('uploadlink', '');
      }
    },
    onLocationChange(value) {
      this.updateField('location', value);
      this.taskInitialized = false;
      this.loadTaskFirstPage(value);
    },
    close() {
      this.$emit('input', false);
    },
    submit() {
      this.$emit('save', {
        mode: this.isEdit ? 'edit' : 'add',
        id: this.request?._id,
        data: {
          ...this.form,
          reference: [...this.internalSelectedTasks],
        },
      });
    },
    async loadTaskFirstPage(locationId) {
      const projectId = this.projectId;
      if (!projectId) return;
      this.taskPage = 1;
      this.taskLoading = true;
      this.taskFullyLoaded = false;
      const effectiveLocationId = locationId || this.form.location || undefined;
      try {
        const result = await this.$store.dispatch('informationManager/fetchChecklistPage', {
          projectId,
          pagination: { page: 1, limit: this.taskLimit },
          search: this.taskSearchQuery || undefined,
          searchFields: ['ref', 'description', 'locationName'],
          locationId: effectiveLocationId,
        });
        this.taskRecords = result.records || [];
        this.taskTotalCount = result.totalCount || 0;
        this.taskFullyLoaded = this.taskRecords.length >= this.taskTotalCount;
        this.taskInitialized = true;
      } catch (err) {
        console.error('[AddEditRequestModal]: loadTaskFirstPage error', err);
        this.taskRecords = [];
      } finally {
        this.taskLoading = false;
      }
    },
    async loadNextTaskPage() {
      if (this.taskLoading || this.taskFullyLoaded) return;
      const projectId = this.projectId;
      if (!projectId) return;
      this.taskPage += 1;
      this.taskLoading = true;
      try {
        const result = await this.$store.dispatch('informationManager/fetchChecklistPage', {
          projectId,
          pagination: { page: this.taskPage, limit: this.taskLimit },
          search: this.taskSearchQuery || undefined,
          searchFields: ['ref', 'description', 'locationName'],
          locationId: this.form.location || undefined,
        });
        const newRecords = result.records || [];
        this.taskRecords = [...this.taskRecords, ...newRecords];
        this.taskTotalCount = result.totalCount || 0;
        this.taskFullyLoaded = this.taskRecords.length >= this.taskTotalCount;
      } catch (err) {
        console.error('[AddEditRequestModal]: loadNextTaskPage error', err);
      } finally {
        this.taskLoading = false;
      }
    },
    onTaskSearch(value) {
      const normalizedQuery = value || '';
      if (normalizedQuery === this.taskSearchQuery) return;
      this.taskSearchQuery = normalizedQuery;
      this.loadTaskFirstPage(this.form.location || undefined);
    },
    onTaskScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        this.loadNextTaskPage();
      }
    },
    toggleSelectAll(isSelected) {
      const shouldSelect = typeof isSelected === 'boolean' ? isSelected : !this.allTasksSelected;
      if (shouldSelect) {
        const merged = new Set([
          ...this.internalSelectedTasks,
          ...this.filteredTaskList.map((option) => option.value),
        ]);
        this.internalSelectedTasks = Array.from(merged);
      } else {
        const filteredValues = new Set(this.filteredTaskList.map((option) => option.value));
        this.internalSelectedTasks = this.internalSelectedTasks.filter((value) => !filteredValues.has(value));
      }
    },
    toggleTask(value) {
      if (this.internalSelectedTasks.includes(value)) {
        this.internalSelectedTasks = this.internalSelectedTasks.filter((item) => item !== value);
      } else {
        this.internalSelectedTasks = [...this.internalSelectedTasks, value];
      }
    },
    removeTask(value) {
      this.internalSelectedTasks = this.internalSelectedTasks.filter((item) => item !== value);
    },
    truncateTaskName(name, maxLength = 22) {
      if (!name) return '';
      if (name.length <= maxLength) return name;
      return `${name.slice(0, maxLength).trimEnd()}...`;
    },
  },
};
</script>
